package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.SitePalettes
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.onInkPalette
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * The page's commercial argument, made by repetition rather than by prose.
 *
 * Twelve cells show one source string and eleven translations. The grid illustrates stored
 * content across languages; the paragraph distinguishes storage from translation usage.
 *
 * **The tally is the payload.** 12 / 47 / 564 / 0. The zero and Studio example price take the
 * accent; neither animates because a price should never briefly show the wrong number.
 *
 * Vendor pricing models vary. This section makes only Lingvo's storage-pricing claim, without
 * turning an illustrative grid into a claim about every competing product.
 */
@Composable
fun MultiplicationBand() {
    Band(ground = BandGround.Ink, labelledBy = MULTIPLICATION_HEADING_ID) {
        Div(MultiplicationBandHeadStyle.toModifier().toAttrs()) {
            Div(MultiplicationBandHeadLeadStyle.toModifier().toAttrs()) {
                // Sentence case; the uppercase is `BandLabelStyle`'s, not the DOM's.
                BandLabel(t("marketing.multiplication_band.the_arithmetic_everyone_else_hides", "What twelve languages store"), onInk = true)
                BandHeading(
                    t(
                        "marketing.multiplication_band.you_wrote_it_once_they_bill",
                        "Twelve languages. No storage meter.",
                    ),
                    id = MULTIPLICATION_HEADING_ID,
                    onInk = true,
                )
            }
            // Written here rather than in a top-level `const` because a lookup needs a composition
            // to sit in, and because `extract-strings.mjs` reads literals at call sites: a constant
            // referenced by name is a sentence no translator is ever offered. It was the largest
            // block of untranslated English on the page.
            P(MultiplicationBandNoteStyle.toModifier().toAttrs()) {
                Text(
                    t(
                        "marketing.multiplication_band.crowdin_transifex_weblate_and_poeditor_price",
                        "Store the source and its translations without a per-word or per-language " +
                            "storage charge. Machine translation uses credits, or you can use " +
                            "your own provider key.",
                    ),
                )
            }
        }

        // A list, not a table: there are no column headings and no row headings, because every cell
        // holds the same one thing. A table would promise a second axis that does not exist.
        Ul(
            MultiplicationBandGridStyle.toModifier()
                .toAttrs { attr("aria-label", tr("marketing.multiplication_band.add_to_cart_in_twelve_languages", "\"Add to cart\", in twelve languages")) },
        ) {
            LOCALES.forEach { locale ->
                Li(MultiplicationBandCellStyle.toModifier().toAttrs()) {
                    Div(
                        MultiplicationBandCodeStyle.toModifier()
                            .then(if (locale.source) MultiplicationBandSourceCodeStyle.toModifier() else Modifier)
                            .toAttrs(),
                    ) {
                        // Deliberately no `lang` here. "ja" is a Latin-script *name* for a language,
                        // not text written in it, and `lang="ja"` would hand those two letters to
                        // Noto Sans JP — the code would set in a different face from its eleven
                        // siblings for no reason a reader could explain.
                        Text(locale.code)
                    }
                    Div(
                        MultiplicationBandStringStyle.toModifier()
                            .then(if (locale.source) MultiplicationBandSourceStringStyle.toModifier() else Modifier)
                            // Load-bearing. Han is unified in Unicode, so 加入购物车 and カートに追加
                            // are drawn by whichever face `:lang()` selects and by nothing else; the
                            // Devanagari and Arabic strings have no face in the Latin stack at all.
                            // Every string carries the attribute, including `en`, so the data drives
                            // it and nobody has to remember which rows are "the foreign ones".
                            .toAttrs { attr("lang", locale.lang) },
                    ) {
                        // No `dir="rtl"` on the Arabic cell. The string is a single unbroken RTL run
                        // with no neutrals, so the bidirectional algorithm already orders it
                        // correctly; `dir` would additionally right-align it inside its cell and
                        // pull one cell out of the grid's left rhythm to no reader's benefit.
                        Text(locale.string)
                    }
                }
            }
        }

        Div(MultiplicationBandTallyStyle.toModifier().toAttrs()) {
            Ul(MultiplicationBandCountsStyle.toModifier().toAttrs()) {
                tally().forEach { count ->
                    Li(MultiplicationBandCountStyle.toModifier().toAttrs()) {
                        Div(
                            MultiplicationBandCountValueStyle.toModifier()
                                .then(if (count.accent) MultiplicationBandAccentFigureStyle.toModifier() else Modifier)
                                .toAttrs(),
                        ) { Text(count.value) }
                        Div(MultiplicationBandCountLabelStyle.toModifier().toAttrs()) { Text(count.label) }
                    }
                }
            }
            Div(MultiplicationBandStillStyle.toModifier().toAttrs()) {
                Div(MultiplicationBandStillLabelStyle.toModifier().toAttrs()) { Text(t("marketing.multiplication_band.your_bill_unchanged", "Studio, one billed seat")) }
                Div(
                    MultiplicationBandPriceStyle.toModifier()
                        .then(MultiplicationBandAccentFigureStyle.toModifier())
                        .toAttrs(),
                ) { Text("\$19") }
            }
        }
    }
}

private const val MULTIPLICATION_HEADING_ID = "multiplication-heading"

private data class LocaleCell(
    val code: String,
    /** BCP-47 tag for the `lang` attribute, which is what selects the face. */
    val lang: String,
    val string: String,
    /** The one cell somebody actually wrote. Everything else is that cell, billed again. */
    val source: Boolean = false,
)

/**
 * Three rows of four, in the design file's order.
 *
 * The order is not alphabetical and not by speaker count — it alternates Latin and non-Latin so no
 * quarter of the grid reads as "the scripts section". `en` leads because it is the source; the
 * remaining eleven are interchangeable and are meant to be.
 */
private val LOCALES = listOf(
    LocaleCell("en", "en", "Add to cart", source = true),
    LocaleCell("ru", "ru", "В корзину"),
    LocaleCell("de", "de", "In den Warenkorb"),
    LocaleCell("ja", "ja", "カートに追加"),
    LocaleCell("ar", "ar", "أضف إلى السلة"),
    LocaleCell("hi", "hi", "कार्ट में डालें"),
    LocaleCell("ko", "ko", "장바구니에 담기"),
    LocaleCell("el", "el", "Στο καλάθι"),
    LocaleCell("pt", "pt", "Adicionar ao carrinho"),
    LocaleCell("zh", "zh", "加入购物车"),
    LocaleCell("fr", "fr", "Ajouter au panier"),
    LocaleCell("es", "es", "Añadir al carrito"),
)

private data class TallyFigure(val value: String, val label: String, val accent: Boolean = false)

/**
 * The last one is the only one that is a claim. The first three are just what a project contains.
 *
 * A function rather than a top-level `val`, for the reason the price card and the limits rows are
 * both keyed at their render sites: a `val` initialises at class-init, before any catalogue is
 * fetched, so a lookup written into it would return its own English for the life of the page. Here
 * it also buys the lookups a composition to run in, which is what makes the four labels under the
 * page's biggest figures translatable at all.
 */
@Composable
private fun tally(): List<TallyFigure> = listOf(
    TallyFigure("12", t("marketing.multiplication_band.languages", "languages")),
    TallyFigure("47", t("marketing.multiplication_band.keys", "keys")),
    TallyFigure("564", t("marketing.multiplication_band.strings", "strings")),
    TallyFigure("0", t("marketing.multiplication_band.words_counted", "stored words billed"), accent = true),
)

/**
 * The hover tint, which is one step off the ground in whichever direction has contrast.
 *
 * On ink that step is lighter (`surface`, `#1C1A16`). On paper it is darker (`surfaceSunken`,
 * `#EDE8DD`) — Day's `surface` is `#FDFBF7` against a `#F6F3EC` ground, a 1.03:1 change that is a
 * hover state in the stylesheet and nothing at all on the screen.
 */
private fun ColorMode.inkCellTint(): Color =
    if (this == ColorMode.LIGHT) SitePalettes.dark.surface else SitePalettes.light.surfaceSunken

/**
 * Heading left, paragraph right, bottoms aligned.
 *
 * The paragraph sits beside the heading rather than under it so the reader reaches the grid without
 * a column of prose first — the grid is the argument and the prose is the footnote. Below the split
 * the two stack, and the gap drops from [Space.Hero] to [Space.XXXL]: 72px between two things side
 * by side is a column gutter, 72px between two stacked things is a missing section.
 *
 * **The split is at [Breakpoint.XL], not [Breakpoint.LG].** At 1024px the band's own gutters leave
 * 840px, and taking a 400px column out of that plus the 72px gutter leaves the heading 368px — a
 * 44px display face broken across five lines, which is a worse first impression than a stacked
 * paragraph. At 1280px the heading gets 624px and sets in two.
 */
val MultiplicationBandHeadStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.XXXL)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("align-items", "end")
            }
    }
    Breakpoint.XL {
        // 25rem is a measure, not a spacing step: it is the width at which this paragraph sets to
        // about three lines, which is what keeps its last line level with the heading's baseline.
        Modifier.gap(Space.Hero).styleModifier {
            property("grid-template-columns", "minmax(0, 1fr) minmax(0, ${25.cssRem})")
        }
    }
}

val MultiplicationBandHeadLeadStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Space.LG.toString())
    }
}

val MultiplicationBandNoteStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.BodySmall)
        .lineHeight(TypeScale.BodyLineHeight)
        .margin(0.px)
        .color(colorMode.onInkPalette().textMuted)
}

/**
 * One rule per edge, never two.
 *
 * The container carries the top and left edges and every cell carries its right and bottom, so
 * adjacent cells share a single hairline instead of stacking two into a 2px seam that only appears
 * on interior gridlines. It also survives reflow for free: at two columns and at one, the cells
 * that end up on an edge already own the rule that closes the box.
 *
 * The left-hand rule is one side of a complete frame, not a marker — the banned device is a
 * *coloured* bar attached to the leading edge of a single element, and this is a 1px border in the
 * band's own boundary tone drawn on all four sides of every cell.
 */
val MultiplicationBandGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(0.px)
            .padding(0.px)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("list-style", "none")
                property("border-top", "1px solid ${colorMode.onInkPalette().borderStrong}")
                property("border-left", "1px solid ${colorMode.onInkPalette().borderStrong}")
            }
    }
    // Twelve divides by four, by two and by one, so the grid stays rectangular at all three widths
    // and never leaves a single orphan cell on a final row.
    Breakpoint.MD { Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") } }
    Breakpoint.LG { Modifier.styleModifier { property("grid-template-columns", "repeat(4, minmax(0, 1fr))") } }
}

/**
 * The tint is the only motion in this band, and it is a response.
 *
 * The motion spec allows a specimen row to tint one step on hover at [Motion.FastMs]; that is what
 * this is. Nothing here lifts — a cell in a shared grid has no gap to lift out of, and lifting one
 * would break the rules its neighbours are drawn with.
 */
val MultiplicationBandCellStyle = CssStyle {
    base {
        Modifier
            .padding(Space.XL)
            .transitionColors(durationMs = Motion.FastMs)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", Space.SM.toString())
                property("border-right", "1px solid ${colorMode.onInkPalette().borderStrong}")
                property("border-bottom", "1px solid ${colorMode.onInkPalette().borderStrong}")
            }
    }
    hover { Modifier.backgroundColor(colorMode.inkCellTint()) }
}

val MultiplicationBandCodeStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .fontWeight(700)
        .letterSpacing(TypeScale.MicroTracking)
        .color(colorMode.onInkPalette().textMuted)
}

/**
 * The source locale, in the accent — the one cell that was written rather than billed.
 *
 * The shared `onInkPalette` helper keeps it the same measured accent as the band label above it in
 * both color modes.
 */
val MultiplicationBandSourceCodeStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().accent)
}

val MultiplicationBandStringStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.Body)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.onInkPalette().textMuted)
}

/** Full paper on the source string, muted on the eleven copies. The difference is the argument. */
val MultiplicationBandSourceStringStyle = CssStyle.base {
    Modifier.color(colorMode.toSitePalette().surfaceBase)
}

val MultiplicationBandTallyStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.XXXL)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("justify-content", "space-between")
            // Baselines would be the better alignment if the two sides shared a type size; they do
            // not, so the bottoms are what line up.
            property("align-items", "flex-end")
        }
}

val MultiplicationBandCountsStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .padding(0.px)
        .gap(Space.Huge)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("list-style", "none")
        }
}

val MultiplicationBandCountStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Space.XS.toString())
    }
}

/**
 * Tabular figures, so 12 / 47 / 564 / 0 sit on one optical line despite four different widths.
 *
 * Tracking is an optical measure rather than a token: the display face is set at roughly -0.035em
 * everywhere it is large, which is -1.5px at [TypeScale.Display] in [BandHeadingStyle] and -1.2px
 * here at [TypeScale.Heading].
 */
val MultiplicationBandCountValueStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.Heading)
        .fontWeight(800)
        .lineHeight(TypeScale.DisplayLineHeight)
        .letterSpacing((-1.2).px)
        .tabularNumbers()
        .color(colorMode.toSitePalette().surfaceBase)
}

/** The two figures the page's argument rests on. Same accent as the label, for the same reason. */
val MultiplicationBandAccentFigureStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().accent)
}

val MultiplicationBandCountLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .letterSpacing(TypeScale.MicroTracking)
        .color(colorMode.onInkPalette().textMuted)
}

val MultiplicationBandStillStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Space.XS.toString())
        property("align-items", "flex-end")
        property("text-align", "right")
    }
}

/**
 * The one tracked label on the page that is right-aligned, and it needs the tracking taken back.
 *
 * CSS adds letter-spacing after every character including the last, so a tracked run flush right
 * hangs a trailing space off its own edge and sits [TypeScale.MicroTracking] left of the price
 * beneath it. Pulling the same amount back off the right margin puts the two edges on one line.
 */
val MultiplicationBandStillLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .letterSpacing(TypeScale.MicroTracking)
        .color(colorMode.onInkPalette().textMuted)
        .styleModifier {
            // The capitals live here, not in the DOM. Written as literal capitals they were read
            // aloud as a shout or spelled out letter by letter — the rule `FormatsBand` and
            // `HeroPitch` both state, and which `BandLabelStyle` already follows.
            property("text-transform", "uppercase")
            property("margin-right", "-${TypeScale.MicroTracking}")
        }
}

/**
 * The price, at [TypeScale.Hero] — a step above the counts beside it, because it is the answer and
 * they are the working.
 *
 * -1.8px is the same -0.035em the display face carries at every large size.
 *
 * 800 rather than the app's 700 ceiling: this is the marketing display face, Bricolage Grotesque is
 * requested across `wght@500..800`, and the same figure is set at 800 in the hero's price card. One
 * number at two weights on one page is the kind of thing a reader notices without being able to say
 * why.
 */
val MultiplicationBandPriceStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.Hero)
        .fontWeight(800)
        .lineHeight(TypeScale.DisplayLineHeight)
        .letterSpacing((-1.8).px)
        .tabularNumbers()
}
